import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '@config/configuration.service';
import { BrokerSettingsFields, SavedBrokerSettings, AdminClientSettings, BrokerSessionStatusFirstFetch } from './broker.interface';
import { map, tap, switchMap } from 'rxjs/operators';
import { BrokerSettings } from './broker-settings.model';
import { of } from 'rxjs';
import { GenericMessageResponse } from '@app/app.interface';
import { MbPlansService } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.service';

@Injectable({
    providedIn: 'root'
})
export class BrokerService {
    brokerSessionStatus: BrokerSessionStatusFirstFetch;
    savedSettingCached: SavedBrokerSettings;
    brokerFormSettingsCache: BrokerSettings;

    constructor(
        private http: HttpClient,
        private conf: ConfigurationService,
        private mbPlansService: MbPlansService
    ) {
    }

    getBrokerFormSettings() {
        if (this.brokerFormSettingsCache) {
            return of(this.brokerFormSettingsCache);
        }
        return this.http.get<BrokerSettingsFields[]>(this.conf.applicationUrls.brokerFormSettings)
            .pipe(
                switchMap((formResponse) => {
                    return this.mbPlansService.getCurrentPlan().pipe(
                        map((planResponse) => {
                            // Filter out brokers which are not configured in this plan.
                            formResponse = formResponse.filter(brokerForm => planResponse.plan.brokers?.includes(brokerForm.Key));
                            this.brokerFormSettingsCache = new BrokerSettings(formResponse);
                            return this.brokerFormSettingsCache;
                        })
                    )
                })
            );
    }

    getBrokerSettings() {
        if (this.savedSettingCached) {
            return of(this.savedSettingCached);
        }
        return this.http.get<SavedBrokerSettings>(this.conf.applicationUrls.brokerSettings).pipe(
            tap((res) => {
                this.savedSettingCached = res;
            })
        );
    }

    saveBrokerFormSettings(broker: string, settings: Object) {
        const data = {
            broker, settings
        };

        return this.http.post(this.conf.applicationUrls.brokerSettings, data).pipe(
            tap((_) => {
                if (!this.savedSettingCached) {
                    this.savedSettingCached = {};
                }
                this.savedSettingCached[broker] = settings as any;
            })
        );
    }

    deleteBrokerFormSettings(broker: string) {
        return this.http.delete(this.conf.applicationUrls.brokerSettings + broker + "/")
            .pipe(
                tap((res) => {
                    delete this.savedSettingCached[broker];
                })
            );
    }

    logoutBroker(broker: string) {
        return this.http.put<GenericMessageResponse>(this.conf.applicationUrls.getBrokerLogoutURL(broker), null);
    }

    getClientsBrokers(clientID: string) {
        return this.http.get<AdminClientSettings>(this.conf.applicationUrls.clientsBrokers + clientID + "/");
    }

    getBrokerSession() {
        if (this.brokerSessionStatus) {
            return of(this.brokerSessionStatus)
        }
        return this.http.get<BrokerSessionStatusFirstFetch>(this.conf.applicationUrls.brokerSession)
    }
}
